import { addMarketplaceEntities } from '../ducks/marketplaceData.duck';
import { listingsAPI } from '../api';
import { storableError } from '../util/errors';
import { types as sdkTypes } from '../util/sdkLoader';
import { userEntity } from '../util/invites';
import { v4 as generateUUID } from 'uuid';

const { UUID } = sdkTypes;

const nonExistingUserEntity = invite => ({
  id: new UUID(invite.id),
  attributes: {
    email: invite.email,
  },
});

// ================ Action types ================ //

/**
 * Listing invites
 */
export const SHOW_LISTING_INVITES_REQUEST = 'app/invites/SHOW_LISTING_INVITES_REQUEST';
export const SHOW_LISTING_INVITES_SUCCESS = 'app/invites/SHOW_LISTING_INVITES_SUCCESS';
export const SHOW_LISTING_INVITES_ERROR = 'app/invites/SHOW_LISTING_INVITES_ERROR';

export const SEND_LISTING_INVITE_REQUEST = 'app/invites/SEND_LISTING_INVITE_REQUEST';
export const SEND_LISTING_INVITE_SUCCESS = 'app/invites/SEND_LISTING_INVITE_SUCCESS';
export const SEND_LISTING_INVITE_ERROR = 'app/invites/SEND_LISTING_INVITE_ERROR';

export const RESEND_LISTING_INVITE_REQUEST = 'app/invites/RESEND_LISTING_INVITE_REQUEST';
export const RESEND_LISTING_INVITE_SUCCESS = 'app/invites/RESEND_LISTING_INVITE_SUCCESS';
export const RESEND_LISTING_INVITE_ERROR = 'app/invites/RESEND_LISTING_INVITE_ERROR';

export const REVOKE_LISTING_INVITE_REQUEST = 'app/invites/REVOKE_LISTING_INVITE_REQUEST';
export const REVOKE_LISTING_INVITE_SUCCESS = 'app/invites/REVOKE_LISTING_INVITE_SUCCESS';
export const REVOKE_LISTING_INVITE_ERROR = 'app/invites/REVOKE_LISTING_INVITE_ERROR';

export const REMOVE_LISTING_INVITE_REQUEST = 'app/invites/REMOVE_LISTING_INVITE_REQUEST';
export const REMOVE_LISTING_INVITE_SUCCESS = 'app/invites/REMOVE_LISTING_INVITE_SUCCESS';
export const REMOVE_LISTING_INVITE_ERROR = 'app/invites/REMOVE_LISTING_INVITE_ERROR';

// ================ Reducer ================ //

const initialState = {
  // listing
  showListingInvitesInProgress: false,
  showListingInvitesError: null,
  sendListingInviteInProgress: false,
  sendListingInviteError: null,
  resendListingInviteInProgress: false,
  resendListingInviteError: null,
  revokeListingInviteInProgress: false,
  revokeListingInviteError: null,
  removeListingInviteInProgress: false,
  removeListingInviteError: null,
  resendListingId: null,
  revokeListingId: null,
  removeListingId: null,
  listingUserRefs: [],
};

export default function invitesReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    /**
     * Listing invites
     */
    case SHOW_LISTING_INVITES_REQUEST:
      return {
        ...state,
        showListingInvitesInProgress: true,
        showListingInvitesError: null,
      };
    case SHOW_LISTING_INVITES_SUCCESS:
      return {
        ...state,
        showListingInvitesInProgress: false,
        showListingInvitesError: null,
        listingUserRefs: payload.teamInvites.map(i =>
          i.existing ? userEntity({ id: new UUID(i.id) }) : nonExistingUserEntity(i)
        ),
      };
    case SHOW_LISTING_INVITES_ERROR:
      return {
        ...state,
        showListingInvitesInProgress: false,
        showListingInvitesError: null,
      };

    case SEND_LISTING_INVITE_REQUEST:
      return {
        ...state,
        sendListingInviteInProgress: true,
        sendListingInviteError: null,
      };
    case SEND_LISTING_INVITE_SUCCESS:
      return {
        ...state,
        sendListingInviteInProgress: false,
        sendListingInviteError: null,
        listingUserRefs: [
          ...state.listingUserRefs,
          payload.invitedUser.type ? userEntity(payload.invitedUser) : payload.invitedUser,
        ],
      };
    case SEND_LISTING_INVITE_ERROR:
      return {
        ...state,
        sendListingInviteInProgress: false,
        sendListingInviteError: null,
      };

    case RESEND_LISTING_INVITE_REQUEST:
      return {
        ...state,
        resendListingInviteInProgress: true,
        resendListingInviteError: null,
        resendListingId: payload.invitedUserId,
      };
    case RESEND_LISTING_INVITE_SUCCESS:
      return {
        ...state,
        resendListingInviteInProgress: false,
        resendListingInviteError: null,
      };
    case RESEND_LISTING_INVITE_ERROR:
      return {
        ...state,
        resendListingInviteInProgress: false,
        resendListingInviteError: null,
      };

    case REVOKE_LISTING_INVITE_REQUEST:
      return {
        ...state,
        revokeListingInviteInProgress: true,
        revokeListingInviteError: null,
        revokeListingId: payload.invitedUserId,
      };
    case REVOKE_LISTING_INVITE_SUCCESS:
      return {
        ...state,
        revokeListingInviteInProgress: false,
        revokeListingInviteError: null,
        revokeListingId: null,
        listingUserRefs: state.listingUserRefs.filter(u => u.id.uuid !== payload.invitedUserId),
      };
    case REVOKE_LISTING_INVITE_ERROR:
      return {
        ...state,
        revokeListingInviteInProgress: false,
        revokeListingInviteError: null,
      };

    case REMOVE_LISTING_INVITE_REQUEST:
      return {
        ...state,
        removeListingInviteInProgress: true,
        removeListingInviteError: null,
        removeListingId: payload.invitedUserId,
      };
    case REMOVE_LISTING_INVITE_SUCCESS:
      return {
        ...state,
        removeListingInviteInProgress: false,
        removeListingInviteError: null,
        removeListingId: null,
        listingUserRefs: state.listingUserRefs.filter(u => u.id.uuid !== payload.invitedUserId),
      };
    case REMOVE_LISTING_INVITE_ERROR:
      return {
        ...state,
        removeListingInviteInProgress: false,
        removeListingInviteError: null,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

/**
 * Listing invites
 */
export const showListingInvitesRequest = () => ({
  type: SHOW_LISTING_INVITES_REQUEST,
});
export const showListingInvitesSuccess = teamInvites => ({
  type: SHOW_LISTING_INVITES_SUCCESS,
  payload: { teamInvites },
});
export const showListingInvitesError = e => ({
  type: SHOW_LISTING_INVITES_ERROR,
  error: true,
  payload: e,
});

export const sendListingInviteRequest = () => ({
  type: SEND_LISTING_INVITE_REQUEST,
});
export const sendListingInviteSuccess = invitedUser => ({
  type: SEND_LISTING_INVITE_SUCCESS,
  payload: { invitedUser },
});
export const sendListingInviteError = e => ({
  type: SEND_LISTING_INVITE_ERROR,
  error: true,
  payload: e,
});

export const resendListingInviteRequest = invitedUserId => ({
  type: RESEND_LISTING_INVITE_REQUEST,
  payload: { invitedUserId },
});
export const resendListingInviteSuccess = invitedUserId => ({
  type: RESEND_LISTING_INVITE_SUCCESS,
  payload: { invitedUserId },
});
export const resendListingInviteError = e => ({
  type: RESEND_LISTING_INVITE_ERROR,
  error: true,
  payload: e,
});

export const revokeListingInviteRequest = invitedUserId => ({
  type: REVOKE_LISTING_INVITE_REQUEST,
  payload: { invitedUserId },
});
export const revokeListingInviteSuccess = invitedUserId => ({
  type: REVOKE_LISTING_INVITE_SUCCESS,
  payload: { invitedUserId },
});
export const revokeListingInviteError = e => ({
  type: REVOKE_LISTING_INVITE_ERROR,
  error: true,
  payload: e,
});

export const removeListingInviteRequest = invitedUserId => ({
  type: REMOVE_LISTING_INVITE_REQUEST,
  payload: { invitedUserId },
});
export const removeListingInviteSuccess = invitedUserId => ({
  type: REMOVE_LISTING_INVITE_SUCCESS,
  payload: { invitedUserId },
});
export const removeListingInviteError = e => ({
  type: REMOVE_LISTING_INVITE_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

/**
 * Listing fns
 */

export const showListingInvites = listingId => async (dispatch, getState, sdk) => {
  dispatch(showListingInvitesRequest());

  try {
    const response = await listingsAPI.show({
      id: listingId,
      include: ['author', 'images'],
      'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
    });
    const listing = response.data.data.data;
    const publicTeam = listing.attributes.publicData.team || [];

    // Invitees without an account are addressed by email, which is kept in the
    // listing's privateData (it used to be in the public team entry). Fetch the
    // addresses for display; older entries may still carry their own.
    let inviteEmails = {};
    if (publicTeam.some(i => !i.existing)) {
      try {
        const emailsResponse = await listingsAPI.teamInviteEmails(listingId);
        inviteEmails = emailsResponse?.data?.data || {};
      } catch (e) {
        inviteEmails = {};
      }
    }
    const teamFromPublicData = publicTeam.map(i =>
      !i.existing && !i.email && inviteEmails[i.id] ? { ...i, email: inviteEmails[i.id] } : i
    );

    if (teamFromPublicData.length > 0) {
      const userPromises = teamFromPublicData
        .filter(i => i.existing)
        .map(async teamMember => {
          try {
            // Use userId if available (new structure), otherwise use id (old structure)
            const userId = teamMember.userId || teamMember.id;
            const userResponse = await sdk.users.show({
              id: userId,
              include: ['profileImage'],
              'fields.image': ['variants.square-small', 'variants.square-small2x'],
            });
            dispatch(addMarketplaceEntities(userResponse));
          } catch (error) {
            console.error('Error fetching user:', error);
          }
        });

      await Promise.all(userPromises);
    }

    dispatch(showListingInvitesSuccess(teamFromPublicData));
    return teamFromPublicData;
  } catch (error) {
    dispatch(showListingInvitesError(storableError(error)));
  }
};

/**
 * Request to save an invite to listing extended data.
 *
 * @param {string} listingId - The ID of the listing.
 * @param {string} invitedUser - Invited user.
 */
export const requestSaveInviteToListingExtendedData = (
  listingId,
  invitedUser,
  newInvite,
  isOwn
) => async (dispatch, getState, sdk) => {
  try {
    const useSdk = isOwn ? sdk.ownListings : listingsAPI;

    const response = await useSdk.show({ id: listingId });
    const listing = isOwn ? response.data.data : response.data.data.data;
    const { team: teamFromPublicData = [] } = listing.attributes.publicData;

    // The address of an invitee without an account is stored privately by the
    // server when the invite is sent; the public entry keeps only the id.
    const { email: _privateEmail, ...publicInvite } = newInvite;
    const updatedTeamForPublicData = [...teamFromPublicData, publicInvite];

    const updateResponse = await useSdk.update(
      {
        id: listingId,
        publicData: {
          team: updatedTeamForPublicData,
        },
      },
      { expand: true }
    );

    dispatch(addMarketplaceEntities(updateResponse));
    return Promise.resolve(updateResponse);
  } catch (error) {
    console.error('Error saving invite:', error);
    return Promise.reject(error);
  }
};

/**
 * Send an invite to the invited user.
 *
 * 1. Fetch invited user by email
 * 2. Save invite to the master account extended data
 */
export const sendListingInvite = (email, formValues, listingId, isOwn) => async (
  dispatch,
  getState,
  sdk
) => {
  dispatch(sendListingInviteRequest());

  try {
    const { userId } = formValues; // Extract userId from formValues

    let invitedUser;
    let inviteData;

    if (userId) {
      // Existing user selected - fetch their data for Redux store
      try {
        const response = await sdk.users.show({
          id: userId,
          include: ['profileImage'],
          'fields.image': ['variants.square-small', 'variants.square-small2x'],
        });
        invitedUser = response.data.data;
        dispatch(addMarketplaceEntities(response));
      } catch (error) {
        console.error('Error fetching user:', error);
        throw new Error('Could not fetch selected user');
      }

      // Prepare invite data for API
      inviteData = {
        userId,
        formValues: {
          credit: formValues.credit,
          year: formValues.year,
        },
      };
    } else if (email) {
      // Email invitation
      const generatedId = generateUUID();
      invitedUser = {
        id: new UUID(generatedId),
        attributes: { email },
      };

      inviteData = {
        email,
        formValues: {
          inviteId: generatedId, // Pass the generated ID
          credit: formValues.credit,
          year: formValues.year,
        },
      };
    } else {
      throw new Error('Either userId or email must be provided');
    }

    // Send invite through secure API (handles email sending server-side)
    let apiResult = { emailSent: false, message: 'Email service unavailable' };
    try {
      const response = await listingsAPI.sendTeamInvite({
        listingId,
        inviteData,
      });
      apiResult = response;
    } catch (apiError) {
      console.error('Failed to send team invite:', apiError);
      apiResult = { emailSent: false, message: 'Failed to send invitation email' };
      // Continue with local update even if email fails
    }

    // Update listing locally with new data structure
    const newInvite = userId
      ? {
          id: userId,
          userId: userId,
          credit: formValues.credit,
          year: formValues.year,
          pending: true,
          existing: true,
        }
      : {
          id: invitedUser.id.uuid,
          email: email,
          credit: formValues.credit,
          year: formValues.year,
          pending: true,
          existing: false,
        };

    await dispatch(
      requestSaveInviteToListingExtendedData(listingId, invitedUser, newInvite, isOwn)
    );

    // Always dispatch success since invite was saved
    dispatch(sendListingInviteSuccess(invitedUser));
    // Return result with actual email status
    return {
      invitedUser,
      emailSent: apiResult.emailSent,
      message: apiResult.message,
    };
  } catch (error) {
    dispatch(sendListingInviteError(storableError(error)));
    throw error;
  }
};

export const resendListingInvite = (
  invitedUserId,
  listingId,
  email,
  formValues
) => async dispatch => {
  dispatch(resendListingInviteRequest(invitedUserId));

  try {
    // A team entry that is an account goes by its id, and nobody is looked up by address. The rest
    // have no account and keep their original invite id. The server checks that the current user
    // may invite to this listing and sends the email itself.
    const { credit, year, existing } = formValues || {};
    const inviteId = invitedUserId?.uuid || invitedUserId;
    const inviteData = existing
      ? { userId: inviteId, formValues: { credit, year } }
      : { email, formValues: { inviteId, credit, year } };
    await listingsAPI.sendTeamInvite({ listingId, inviteData });

    // Always dispatch success since invite is still active
    dispatch(resendListingInviteSuccess(invitedUserId));

    return;
  } catch (e) {
    dispatch(resendListingInviteError(storableError(e)));
  }
};

export const revokeListingInvite = (listingId, invitedUserId, isOwn) => async (
  dispatch,
  getState,
  sdk
) => {
  dispatch(revokeListingInviteRequest(invitedUserId));

  const listingResponse = await listingsAPI.show({ id: listingId });
  const listing = listingResponse.data.data.data;

  const teamFromPublicData = listing.attributes.publicData.team || [];
  const teamForPublicData = teamFromPublicData.filter(i => i.id !== invitedUserId);

  const useSdk = isOwn ? sdk.ownListings : listingsAPI;

  return useSdk
    .update({
      id: listingId,
      publicData: {
        team: teamForPublicData,
      },
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(revokeListingInviteSuccess(invitedUserId));
      return response;
    })
    .catch(e => dispatch(revokeListingInviteError(storableError(e))));
};

/**
 * Removes an invite for a user and updates their team and organizations.
 *
 */
export const removeListingInvite = (listingId, invitedUserId, isOwn) => async (
  dispatch,
  getState,
  sdk
) => {
  dispatch(removeListingInviteRequest(invitedUserId));

  const listingResponse = await listingsAPI.show({ id: listingId });
  const listing = listingResponse.data.data.data;

  const teamFromPublicData = listing.attributes.publicData.team || [];
  const teamForPublicData = teamFromPublicData.filter(i => i.id !== invitedUserId);

  const useSdk = isOwn ? sdk.ownListings : listingsAPI;

  return useSdk
    .update({
      id: listingId,
      publicData: {
        team: teamForPublicData,
      },
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(removeListingInviteSuccess(invitedUserId));
      return response;
    })
    .catch(e => dispatch(removeListingInviteError(storableError(e))));
};
